import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { centralApi } from '@marque/api/central'
import type { RoleTemplateItem } from '@marque/api/central'

function PatternsEditor({
    roleCode,
    patterns,
    onSaved,
}: {
    roleCode: string
    patterns: string[]
    onSaved: () => void
}) {
    const [editing, setEditing] = useState(false)
    const [draft, setDraft] = useState(patterns.join('\n'))
    const qc = useQueryClient()

    const save = useMutation({
        mutationFn: () => {
            const lines = draft.split('\n').map(l => l.trim()).filter(Boolean)
            return centralApi.updateRoleTemplate(roleCode, lines)
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['role-templates'] })
            setEditing(false)
            onSaved()
        },
    })

    if (!editing) {
        return (
            <div className="flex items-start gap-3">
                <div className="flex-1 flex flex-wrap gap-1">
                    {patterns.map(p => (
                        <code key={p} className="text-xs bg-neutral-100 text-neutral-700 px-1.5 py-0.5 rounded">
                            {p}
                        </code>
                    ))}
                </div>
                <button
                    onClick={() => { setDraft(patterns.join('\n')); setEditing(true) }}
                    className="text-xs text-blue-600 hover:underline shrink-0"
                >
                    Edit
                </button>
            </div>
        )
    }

    return (
        <div className="space-y-2">
            <textarea
                className="w-full text-xs font-mono border border-neutral-300 rounded p-2 h-32 resize-y"
                value={draft}
                onChange={e => setDraft(e.target.value)}
                placeholder="One pattern per line, e.g. sales.*:*"
            />
            <div className="flex items-center gap-2">
                <button
                    onClick={() => save.mutate()}
                    disabled={save.isPending}
                    className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 disabled:opacity-50"
                >
                    {save.isPending ? 'Saving…' : 'Save'}
                </button>
                <button
                    onClick={() => setEditing(false)}
                    className="text-xs text-neutral-600 hover:underline"
                >
                    Cancel
                </button>
                {save.isError && (
                    <span className="text-xs text-red-600">Save failed</span>
                )}
            </div>
        </div>
    )
}

export function RoleTemplatesPage() {
    const qc = useQueryClient()
    const { data, isLoading, isError } = useQuery({
        queryKey: ['role-templates'],
        queryFn: centralApi.getRoleTemplates,
        staleTime: 60_000,
    })

    const pushAll = useMutation({
        mutationFn: (mode: 'merge' | 'replace') =>
            centralApi.pushAllRoleTemplates(mode),
        onSuccess: () => qc.invalidateQueries({ queryKey: ['role-templates'] }),
    })

    const [pushResult, setPushResult] = useState<string | null>(null)

    const handlePush = async (mode: 'merge' | 'replace') => {
        setPushResult(null)
        const res = await pushAll.mutateAsync(mode)
        const ok = res.results.filter(r => !r.error).length
        const fail = res.results.filter(r => r.error).length
        setPushResult(`Pushed to ${ok} tenant(s)${fail > 0 ? `, ${fail} failed` : ''}.`)
    }

    const templates: RoleTemplateItem[] = data?.items ?? []

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <h1 className="text-2xl font-semibold text-neutral-900">Default role templates</h1>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => handlePush('merge')}
                        disabled={pushAll.isPending}
                        className="text-sm bg-white border border-neutral-300 text-neutral-700 px-3 py-1.5 rounded hover:bg-neutral-50 disabled:opacity-50"
                    >
                        Push all (merge)
                    </button>
                    <button
                        onClick={() => handlePush('replace')}
                        disabled={pushAll.isPending}
                        className="text-sm bg-blue-600 text-white px-3 py-1.5 rounded hover:bg-blue-700 disabled:opacity-50"
                    >
                        Push all (replace)
                    </button>
                </div>
            </div>

            <p className="text-sm text-neutral-500">
                These patterns define the default permissions for each built-in role when a new tenant is provisioned,
                and can be pushed to existing tenants on demand.
                <br />
                <strong>Merge</strong> — adds any missing permissions.{' '}
                <strong>Replace</strong> — deletes existing built-in role permissions and re-inserts from patterns.
            </p>

            {pushResult && (
                <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded px-3 py-2">
                    {pushResult}
                </p>
            )}

            {isLoading && <p className="text-sm text-neutral-500">Loading…</p>}
            {isError && <p className="text-sm text-red-600">Failed to load role templates.</p>}

            {templates.length > 0 && (
                <div className="space-y-4">
                    {templates.map(t => (
                        <div key={t.roleCode} className="bg-white border border-neutral-200 rounded-lg p-5 space-y-3">
                            <div className="flex flex-wrap items-center justify-between gap-3">
                                <div>
                                    <p className="text-sm font-semibold text-neutral-900">{t.name}</p>
                                    <p className="text-xs font-mono text-neutral-400">{t.roleCode}</p>
                                </div>
                                <p className="text-xs text-neutral-400">
                                    Updated {new Date(t.updatedAt).toLocaleDateString()}
                                </p>
                            </div>
                            <PatternsEditor
                                roleCode={t.roleCode}
                                patterns={t.patterns}
                                onSaved={() => qc.invalidateQueries({ queryKey: ['role-templates'] })}
                            />
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}
